import React, { useEffect, useState, useCallback } from "react";
import {
    Modal, Form, Button, Divider, Select, InputNumber,
} from "antd";
import {
    SaveOutlined, CloseOutlined, VideoCameraAddOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { GetVrThreeSixtyProjects } from "../../Redux/Actions/homeVrActions";
import {
    viewCountryListPagination,
    viewStateListPagination,
    viewCityListPagination,
} from "../../Redux/Actions/settingsActions";
import "./addAndEditHomeVR.css";

const { Option } = Select;

const AddAndEditHomeVr = ({ visible, onClose, vrData, onSave }) => {
    const dispatch = useDispatch();
    const [form]   = Form.useForm();

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState,   setSelectedState]   = useState(null);

    // Redux state — same keys as AddBuilder
    const vrState            = useSelector((s) => s.vrHome || {});
    const threeSixtyProjects = vrState.threeSixtyProjects || [];

    const { countryList } = useSelector((s) => s.country  || {});
    const { states }      = useSelector((s) => s.states   || {});
    const { cities }      = useSelector((s) => s.city     || {});

    const countryData = countryList?.data || [];
    const stateData   = states?.data      || [];
    const cityData    = cities?.data      || [];

    // Fetch countries + 360 projects on open
    useEffect(() => {
        if (!visible) return;
        dispatch(GetVrThreeSixtyProjects());
        dispatch(viewCountryListPagination());
    }, [visible, dispatch]);

    // Fetch states when country changes
    useEffect(() => {
        if (!selectedCountry) return;
        dispatch(viewStateListPagination({ country: selectedCountry }));
    }, [selectedCountry, dispatch]);

    // Fetch cities when state changes
    useEffect(() => {
        if (!selectedState) return;
        dispatch(viewCityListPagination({ state: selectedState, country: selectedCountry }));
    }, [selectedState, dispatch]);

    // Populate form on edit
    useEffect(() => {
        if (visible && vrData) {
            const country = vrData.country || undefined;
            const state   = vrData.state   || undefined;
            const city    = vrData.city    || undefined;

            setSelectedCountry(country || null);
            setSelectedState(state     || null);

            form.setFieldsValue({
                project:         vrData.project         || undefined,
                country:         country,
                state:           state,
                city:            city,
                project_ranking: vrData.project_ranking ?? 0,
            });
        } else if (visible) {
            form.resetFields();
            setSelectedCountry(null);
            setSelectedState(null);
        }
    }, [visible, vrData, form]);

    const handleCountryChange = (val) => {
        setSelectedCountry(val);
        setSelectedState(null);
        form.setFieldsValue({ state: undefined, city: undefined });
    };

    const handleStateChange = (val) => {
        setSelectedState(val);
        form.setFieldsValue({ city: undefined });
    };

    const handleFinish = async (values) => {
        const payload = {
            project:         values.project,
            country:         values.country,
            state:           values.state,
            city:            values.city,
            project_ranking: values.project_ranking ?? 0,
        };
        await onSave(payload);
    };

    return (
        <Modal
            title={
                <div className="hb-modal-header-content">
                    <VideoCameraAddOutlined className="hb-modal-header-icon" style={{ color: "white" }} />
                    <span style={{ color: "white" }}>
                        {vrData?._id ? "EDIT HOME VR" : "ADD HOME VR"}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
                    onClick={onClose}
                    style={{
                        marginTop: 1, marginRight: 47, padding: 6,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid white", color: "white",
                    }}
                    className="hb-modal-close-btn"
                />
            }
            destroyOnClose
            width={700}
            className="hb-modal-override"
            styles={{
                header: {
                    background: "#070C18",
                    borderRadius: "12px 12px 0 0",
                    padding: "20px 24px",
                    borderBottom: "none",
                },
                body: { padding: "24px", background: "#fafafa" },
            }}
        >
            <div className="hb-modal-form">
                <Form form={form} layout="vertical" onFinish={handleFinish}>

                    {/* Project */}
                    <div className="hb-form-section">
                        <h3 className="hb-form-section-header">Project Details</h3>

                        <Form.Item
                            label="Project"
                            name="project"
                            rules={[{ required: true, message: "Please select a project" }]}
                        >
                            <Select
                                size="large"
                                placeholder="Select project"
                                className="hb-form-input"
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                notFoundContent="No projects available"
                            >
                                {threeSixtyProjects.map((p) => (
                                    <Option key={p._id} value={p._id}>
                                        {p.project_name || p.name || "—"}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label="Project Ranking" name="project_ranking">
                            <InputNumber
                                size="large"
                                min={0}
                                placeholder="Enter ranking"
                                style={{ width: "100%", borderRadius: 8 }}
                            />
                        </Form.Item>
                    </div>

                    <Divider className="hb-form-divider" />

                    {/* Location */}
                    <div className="hb-form-section">
                        <h3 className="hb-form-section-header">Location</h3>

                        <div className="hb-form-grid">

                            {/* Country */}
                            <Form.Item label="Country" name="country">
                                <Select
                                    size="large"
                                    placeholder="Select country"
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    onChange={handleCountryChange}
                                    notFoundContent="No countries available"
                                >
                                    {countryData.map((c) => (
                                        <Option key={c._id} value={c._id}>
                                            {c.country_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* State — disabled until country selected */}
                            <Form.Item label="State" name="state">
                                <Select
                                    size="large"
                                    placeholder={!selectedCountry ? "Select country first" : "Select state"}
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    disabled={!selectedCountry}
                                    onChange={handleStateChange}
                                    notFoundContent="No states available"
                                >
                                    {stateData.map((s) => (
                                        <Option key={s._id} value={s._id}>
                                            {s.state_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* City — disabled until state selected */}
                            <Form.Item label="City" name="city">
                                <Select
                                    size="large"
                                    placeholder={!selectedState ? "Select state first" : "Select city"}
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    disabled={!selectedState}
                                    notFoundContent="No cities available"
                                >
                                    {cityData.map((c) => (
                                        <Option key={c._id} value={c._id}>
                                            {c.city_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </div>
                    </div>

                    <Divider className="hb-form-divider" />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <div className="hb-form-actions">
                            <Button size="large" onClick={onClose} className="hb-btn hb-btn-cancel">
                                Cancel
                            </Button>
                            <Button
                                htmlType="submit"
                                size="large"
                                icon={<SaveOutlined />}
                                className="hb-btn hb-btn-primary"
                            >
                                <span style={{ color: "white" }}>
                                    {vrData?._id ? "Update VR" : "Add VR"}
                                </span>
                            </Button>
                        </div>
                    </Form.Item>

                </Form>
            </div>
        </Modal>
    );
};

export default AddAndEditHomeVr;